import React from 'react';

import { WidgetsMapComponentKeys } from 'config/widgetConfigurations/widgetsConfigurations';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { prepareVersionSelectTitle } from 'components/common/TreeProgramFilter/VersionSelect/versionSelect.utils';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Tabs } from 'components/DesignSystem/Tabs';
import { useFinishDatePredictionCompletionPlanLogic } from 'components/widgets/FinishDatePredictionCompletionPlan/FinishDatePredictionCompletionPlan.logic';
import { useFinishDatePredictionCompletionPlanProvider } from 'components/widgets/FinishDatePredictionCompletionPlan/FinishDatePredictionCompletionPlan.provider';
import {
    IActivityCompletionList,
    TKeys,
    columnsV8,
    initialState,
} from 'components/widgets/FinishDatePredictionCompletionPlan/FinishDatePredictionCompletionPlan.utils';
import { FragnetSummary } from 'components/widgets/FinishDatePredictionCompletionPlan/fragnetSummary';
import { SortFilterPanel } from 'components/widgets/FinishDatePredictionCompletionPlan/sortFilterPanel';

import classes from './FinishDatePredictionCompletionPlan.module.scss';

const id = 'finishDate-activities-predecessors';
const componentKey: WidgetsMapComponentKeys = 'finishDateActivitiesPredecessors';

export const FinishDatePredictionCompletionPlan = ({
    projectId,
    contractId,
    latestVersionId,
    globalFilterData,
    widgetId,
    localFilters,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,

    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = `Completion Plan (Per Current Progress)`,
}: IComponentProps) => {
    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
        widgetViewMode,
    });

    const { ui, api } = useFinishDatePredictionCompletionPlanProvider({
        componentKey,
        widgetId,
        contractId,
        latestVersionId,
        localFilters,
        globalFilterData,
        table,
        widgetViewMode,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        customSubTitle: `${executionConfig.completionPlan.title}, ${prepareVersionSelectTitle()}`,
        ui,
        reportWidgetRef,
        onAddWidgetCallback: table.setWidgetColumns,
    });

    const { logic } = useFinishDatePredictionCompletionPlanLogic({ table, ui, api, title, subTitle });

    return (
        <WidgetWithTitle
            id={id}
            title={<WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />}
            className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                    shouldHide={true}
                    callBack={table.setWidgetColumns}
                    shouldUseNewIcon={true}
                />,
                <AddToReport
                    key={'addWidgetToReport'}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    projectId={projectId}
                    contractId={contractId}
                    uiState={ui.state}
                    callBack={table.setWidgetColumns}
                    shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                />,
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ExportDropdown
                        key={'export'}
                        title={title}
                        subTitle={subTitle}
                        activeTabTitle={ui.state.activeTab}
                        handleAPICsvExport={logic.handleAPICsvExport}
                    />
                ),
                !WidgetViewModeUtils.isReportView(widgetViewMode) && (
                    <ColumnsManager
                        onOpenModal={() => table.setModalColumnsOpen(true)}
                        onHideModal={() => table.setModalColumnsOpen(false)}
                        showModal={table.modalColumnsOpen}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        tableInstance={table.tableInstance}
                        initialState={initialState}
                        columns={columnsV8}
                        onChangeOrder={table.setColumnOrder}
                        onChangeVisibility={table.setColumnVisibility}
                        widgetViewMode={widgetViewMode}
                    />
                ),
                <TooltipIcon key={'info'} tooltip={api.getActivityCompletionPlan.data?.info} />,
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ThreeDotsMenu
                        widgetViewMode={widgetViewMode}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        shareTitle={title}
                        shareLink={route}
                        projectId={projectId}
                        contractId={contractId}
                    />
                ),
            ]}
            titleFilters={[
                <ReportingVersionAndPills
                    key={'ReportingVersionAndPills'}
                    selectedProjectAndContract={widgetSelectedProjectAndContract}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                />,
                <Narrative key={'narrative'} editNarrative={editNarrative} />,
                <PinboardPills
                    key={'pinboardPills'}
                    isInPinboard={ui.isInPinboard}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                />,
            ]}
        >
            <TableContainer className={classes.tableWrapperContainer}>
                {logic.tabsArrData.length > 0 && api.getActivityCompletionPlan.data && (
                    <FragnetSummary data={api.getActivityCompletionPlan.data} />
                )}
                {logic.tabsArrData.length > 0 && (
                    <Tabs
                        tabs={logic.tabsArrData}
                        value={ui.state.activeTab}
                        onChange={(value) => ui.setters.onSetActiveTab(value as TKeys)}
                    />
                )}
                {!api.getActivityCompletionPlan.isLoading &&
                    WidgetViewModeUtils.isPageOrReportAddOrEditWidget(widgetViewMode) &&
                    logic.tabsArrData.length > 0 && (
                        <SortFilterPanel
                            taskVersionHashCode={ui.vars.taskVersionHashCode}
                            hashcode={logic.hashcode}
                            ui={ui}
                            widgetViewMode={widgetViewMode}
                        />
                    )}

                {table.updatedInitialState && (
                    <TableV8<IActivityCompletionList>
                        data={api.getActivityCompletionPlan.data?.activity_completion_list || []}
                        columns={columnsV8}
                        isLoading={api.getActivityCompletionPlan.isLoading || api.getActivityCompletionPlan.isFetching}
                        noData={
                            <NoData>
                                To view Completion Plan for the <strong>Project Finish</strong> activity select the{' '}
                                <strong>first</strong> activity in fragnet filter list above.
                                <br />
                                To view Completion Plan for a <strong>specific</strong> milestone/activity select it
                                from the fragnet filter list above.
                            </NoData>
                        }
                        sorting={table.sorting}
                        setSorting={table.setSorting}
                        columnVisibility={table.columnVisibility}
                        setColumnVisibility={table.setColumnVisibility}
                        columnOrder={table.columnOrder}
                        setColumnOrder={table.setColumnOrder}
                        rowSelection={table.rowSelection}
                        setRowSelection={table.setRowSelection}
                        totalPages={table.totalPages}
                        pagination={table.pagination}
                        setPagination={table.setPagination}
                        isPaginationDisabled={api.getActivityCompletionPlan.isFetching}
                        displayPaginationBottomBar
                    />
                )}
            </TableContainer>
        </WidgetWithTitle>
    );
};
